import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import DOMPurify from "dompurify";
import {
  api,
  type MailFolder,
  type MailMessageDetail,
  type MailMessageSummary,
  type Preferences,
  type PublicMailAccount,
} from "../../lib/api";
import { useToast } from "../shell/Toast";
import { ComposerModal } from "./ComposerModal";
import { AccountWizard } from "./AccountWizard";

type MobilePane = "nav" | "list" | "read";
type SmartId = "" | "unread" | "starred" | "attachments" | "today";
type SortId =
  | "date-desc"
  | "date-asc"
  | "from-asc"
  | "from-desc"
  | "subject-asc"
  | "subject-desc";
type DateFilterId = "all" | "today" | "yesterday" | "7d" | "30d" | "month" | "custom";

function loadPref<T extends string>(key: string, fallback: T): T {
  try {
    return (localStorage.getItem(key) as T) || fallback;
  } catch {
    return fallback;
  }
}

function loadWidth(key: string, fallback: number, min: number, max: number): number {
  try {
    const n = Number(localStorage.getItem(key));
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  } catch {
    return fallback;
  }
}

export function MailWorkspace({ prefs }: { prefs: Preferences | null }) {
  const toast = useToast();
  const [accounts, setAccounts] = useState<PublicMailAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | "all">("all");
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [folder, setFolder] = useState("INBOX");
  const [smart, setSmart] = useState<SmartId>("");
  const [messages, setMessages] = useState<MailMessageSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<MailMessageSummary | null>(null);
  const [detail, setDetail] = useState<MailMessageDetail | null>(null);
  const [search, setSearch] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [blockingLoad, setBlockingLoad] = useState(false);
  const [error, setError] = useState("");
  const [composer, setComposer] = useState(false);
  const [wizard, setWizard] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mobile, setMobile] = useState<MobilePane>("list");
  const [railExpanded, setRailExpanded] = useState(() => {
    try {
      return localStorage.getItem("mailstorm.railExpanded") === "1";
    } catch {
      return true;
    }
  });
  const [sortBy, setSortBy] = useState<SortId>(() => loadPref("mailstorm.sortBy", "date-desc"));
  const [dateFilter, setDateFilter] = useState<DateFilterId>(() => loadPref("mailstorm.dateFilter", "all"));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [folderWidth, setFolderWidth] = useState(() => loadWidth("mailstorm.folderWidth", 220, 160, 420));
  const [listWidth, setListWidth] = useState(() => loadWidth("mailstorm.listWidth", 460, 280, 720));
  const [dragAccountId, setDragAccountId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const loadGen = useRef(0);
  const defaultApplied = useRef(false);
  const resizing = useRef<"folder" | "list" | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem("mailstorm.railExpanded", railExpanded ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [railExpanded]);

  useEffect(() => {
    try {
      localStorage.setItem("mailstorm.sortBy", sortBy);
      localStorage.setItem("mailstorm.dateFilter", dateFilter);
      localStorage.setItem("mailstorm.folderWidth", String(folderWidth));
      localStorage.setItem("mailstorm.listWidth", String(listWidth));
    } catch {
      /* ignore */
    }
  }, [sortBy, dateFilter, folderWidth, listWidth]);

  const refreshAccounts = useCallback(async () => {
    const res = await api.accounts();
    setAccounts(res.accounts);
    if (!defaultApplied.current && prefs?.defaultAccountId) {
      defaultApplied.current = true;
      setSelectedAccountId(prefs.defaultAccountId);
    } else if (!defaultApplied.current) {
      defaultApplied.current = true;
    }
  }, [prefs?.defaultAccountId]);

  const loadFoldersFor = useCallback(async (accountId: string | "all") => {
    if (accountId === "all") {
      setFolders([
        { path: "INBOX", name: "Inbox", specialUse: "\\Inbox", delimiter: "/", listed: true, subscribed: true },
        { path: "Sent", name: "Sent", specialUse: "\\Sent", delimiter: "/", listed: true, subscribed: true },
        { path: "Drafts", name: "Drafts", specialUse: "\\Drafts", delimiter: "/", listed: true, subscribed: true },
        { path: "Trash", name: "Trash", specialUse: "\\Trash", delimiter: "/", listed: true, subscribed: true },
        { path: "Junk", name: "Spam", specialUse: "\\Junk", delimiter: "/", listed: true, subscribed: true },
      ]);
      return;
    }
    try {
      const res = await api.folders(accountId);
      setFolders(res.folders);
    } catch {
      setFolders([]);
    }
  }, []);

  const loadMessagesFor = useCallback(
    async (
      accountId: string | "all",
      folderPath: string,
      smartId: SmartId,
      searchQ: string,
      opts?: { bustCache?: boolean },
    ) => {
      const res = await api.messages({
        accountId: accountId === "all" ? undefined : accountId,
        folder: folderPath,
        page: 1,
        pageSize: 100,
        search: searchQ.trim() || undefined,
        smart: smartId || undefined,
        bustCache: opts?.bustCache,
      });
      setMessages(res.messages);
      setTotal(res.total);
    },
    [],
  );

  const refreshMailbox = useCallback(
    async (opts?: { blocking?: boolean; bustCache?: boolean; searchQ?: string }) => {
      const gen = ++loadGen.current;
      if (opts?.blocking) setBlockingLoad(true);
      setBusy(true);
      setError("");
      const q = opts?.searchQ !== undefined ? opts.searchQ : committedSearch;
      try {
        await loadFoldersFor(selectedAccountId);
        if (gen !== loadGen.current) return;
        await loadMessagesFor(selectedAccountId, folder, smart, q, {
          bustCache: opts?.bustCache,
        });
      } catch (e) {
        if (gen !== loadGen.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setMessages([]);
        setTotal(0);
      } finally {
        if (gen === loadGen.current) {
          setBusy(false);
          setBlockingLoad(false);
        }
      }
    },
    [committedSearch, folder, loadFoldersFor, loadMessagesFor, selectedAccountId, smart],
  );

  useEffect(() => {
    void refreshAccounts();
  }, [refreshAccounts]);

  useEffect(() => {
    void refreshMailbox();
  }, [refreshMailbox]);

  function selectAccount(id: string | "all") {
    if (id === selectedAccountId) return;
    setBlockingLoad(true);
    setBusy(true);
    setSelectedAccountId(id);
    setFolder("INBOX");
    setSmart("");
    setSelected(null);
    setDetail(null);
    setMessages([]);
    setTotal(0);
    setSelectedIds(new Set());
    setMobile("list");
    setError("");
  }

  function startColResize(which: "folder" | "list", e: ReactMouseEvent) {
    e.preventDefault();
    resizing.current = which;
    const startX = e.clientX;
    const startFolder = folderWidth;
    const startList = listWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    function onMove(ev: MouseEvent) {
      const dx = ev.clientX - startX;
      if (resizing.current === "folder") {
        setFolderWidth(Math.min(420, Math.max(160, startFolder + dx)));
      } else if (resizing.current === "list") {
        setListWidth(Math.min(720, Math.max(280, startList + dx)));
      }
    }
    function onUp() {
      resizing.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  async function onAccountDrop(targetId: string) {
    if (!dragAccountId || dragAccountId === targetId) {
      setDragAccountId(null);
      setDragOverId(null);
      return;
    }
    const ids = accounts.map((a) => a.id);
    const from = ids.indexOf(dragAccountId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, dragAccountId);
    const reordered = ids
      .map((id) => accounts.find((a) => a.id === id)!)
      .filter(Boolean)
      .map((a, i) => ({ ...a, sortOrder: i }));
    setAccounts(reordered);
    setDragAccountId(null);
    setDragOverId(null);
    try {
      const res = await api.reorderAccounts(ids);
      setAccounts(res.accounts);
      toast.push({ message: "Ordine account salvato" });
    } catch (e) {
      toast.push({ message: e instanceof Error ? e.message : "Riordino fallito" });
      await refreshAccounts();
    }
  }

  const refreshMessages = useCallback(
    async (opts?: { bustCache?: boolean }) => {
      const q = search.trim();
      setCommittedSearch(q);
      await refreshMailbox({ bustCache: opts?.bustCache ?? true, searchQ: q });
    },
    [refreshMailbox, search],
  );

  useEffect(() => {
    function onCompose() {
      setComposer(true);
    }
    window.addEventListener("mailstorm:compose", onCompose);
    return () => window.removeEventListener("mailstorm:compose", onCompose);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === "j") moveSelection(1);
      if (e.key === "k") moveSelection(-1);
      if (e.key === "r" && selected) setComposer(true);
      if (e.key === "e" && selected) void archiveSelected();
      if (e.key === "#" && selected) void trashSelected();
      if (e.key === "!" && selected) void spamSelected();
      if (e.key === "s" && selected) void toggleStar();
      if (e.key === "u" && selected) void toggleSeen();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  function keyOf(m: MailMessageSummary) {
    return `${m.accountId}:${m.folder}:${m.uid}`;
  }

  function moveSelection(delta: number) {
    if (!viewMessages.length) return;
    const idx = selected
      ? viewMessages.findIndex((m) => keyOf(m) === keyOf(selected))
      : -1;
    const next = viewMessages[Math.max(0, Math.min(viewMessages.length - 1, idx + delta))];
    if (next) void openMessage(next);
  }

  async function openMessage(msg: MailMessageSummary) {
    setSelected(msg);
    setMobile("read");
    setDetail(null);
    try {
      const res = await api.message(msg.accountId, msg.folder, msg.uid);
      setDetail(res.message);
      const seen = Boolean(res.message.seen);
      setSelected({ ...msg, ...res.message, seen });
      setMessages((prev) =>
        prev.map((m) => (keyOf(m) === keyOf(msg) ? { ...m, seen } : m)),
      );
      if (!msg.seen && !seen) {
        toast.push({ message: "Attenzione: stato «letto» non salvato sul server IMAP" });
      }
      const invite = (res as { invite?: { title?: string; uid?: string } | null }).invite;
      if (invite?.title) {
        toast.push({
          message: `Invite: ${invite.title}`,
          actionLabel: "Accept",
          onAction: () => {
            void (async () => {
              const cals = await api.calendars();
              const calendarId = cals.calendars[0]?.id;
              if (!calendarId) {
                toast.push({ message: "Add a calendar first" });
                return;
              }
              const start = new Date();
              const end = new Date(start.getTime() + 60 * 60 * 1000);
              await api.acceptInvite({
                calendarId,
                title: invite.title,
                start: start.toISOString(),
                end: end.toISOString(),
                uid: invite.uid,
              });
              toast.push({ message: "Invite accepted to calendar" });
            })();
          },
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggleStar() {
    if (!selected) return;
    const flagged = !selected.flagged;
    await api.flags({ accountId: selected.accountId, folder: selected.folder, uid: selected.uid, flagged });
    setSelected({ ...selected, flagged });
    setMessages((prev) =>
      prev.map((m) => (keyOf(m) === keyOf(selected) ? { ...m, flagged } : m)),
    );
  }

  async function toggleSeen() {
    if (!selected) return;
    const seen = !selected.seen;
    await api.flags({ accountId: selected.accountId, folder: selected.folder, uid: selected.uid, seen });
    setSelected({ ...selected, seen });
    setMessages((prev) => prev.map((m) => (keyOf(m) === keyOf(selected) ? { ...m, seen } : m)));
  }

  function findFolder(kind: "archive" | "trash" | "spam") {
    if (kind === "archive") {
      return folders.find((f) => f.specialUse === "\\Archive" || /archive|tutti i messaggi/i.test(f.name))?.path || "Archive";
    }
    if (kind === "trash") {
      return (
        folders.find((f) => f.specialUse === "\\Trash" || /trash|cestino|deleted/i.test(f.name))?.path || "Trash"
      );
    }
    return folders.find((f) => f.specialUse === "\\Junk" || /junk|spam/i.test(f.name))?.path || "Junk";
  }

  async function moveSelected(kind: "archive" | "trash" | "spam", label: string) {
    if (!selected) return;
    const target = findFolder(kind);
    const snapshot = selected;
    await api.move({
      accountId: selected.accountId,
      folder: selected.folder,
      uid: selected.uid,
      targetFolder: target,
    });
    setMessages((prev) => prev.filter((m) => keyOf(m) !== keyOf(snapshot)));
    setSelected(null);
    setDetail(null);
    toast.push({
      message: label,
      actionLabel: "Annulla",
      onAction: () => {
        void api
          .undo({
            accountId: snapshot.accountId,
            fromFolder: target,
            toFolder: snapshot.folder,
            uid: snapshot.uid,
          })
          .then(() => refreshMessages());
      },
    });
  }

  async function archiveSelected() {
    await moveSelected("archive", "Archiviato");
  }
  async function trashSelected() {
    await moveSelected("trash", "Spostato nel cestino");
  }
  async function spamSelected() {
    await moveSelected("spam", "Segnato come spam");
  }

  async function bulkAction(action: "seen" | "unseen" | "star" | "trash") {
    const items = messages.filter((m) => selectedIds.has(keyOf(m)));
    if (!items.length) return;
    await api.bulk({ action, items: items.map((m) => ({ accountId: m.accountId, folder: m.folder, uid: m.uid })) });
    setSelectedIds(new Set());
    await refreshMessages();
    toast.push({ message: `Bulk ${action} applied` });
  }

  const viewMessages = useMemo(() => {
    const now = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const inRange = (iso: string | null) => {
      if (dateFilter === "all") return true;
      if (!iso) return false;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return false;
      const today = startOfDay(now);
      if (dateFilter === "today") return d >= today;
      if (dateFilter === "yesterday") {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        return d >= y && d < today;
      }
      if (dateFilter === "7d") {
        const from = new Date(today);
        from.setDate(from.getDate() - 7);
        return d >= from;
      }
      if (dateFilter === "30d") {
        const from = new Date(today);
        from.setDate(from.getDate() - 30);
        return d >= from;
      }
      if (dateFilter === "month") {
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      }
      if (dateFilter === "custom") {
        const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
        const to = dateTo ? new Date(`${dateTo}T23:59:59`) : null;
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      }
      return true;
    };

    const filtered = messages.filter((m) => inRange(m.date));
    const fromKey = (m: MailMessageSummary) => (m.from || m.accountEmail || "").toLowerCase();
    const subKey = (m: MailMessageSummary) => (m.subject || "").toLowerCase();
    const dateKey = (m: MailMessageSummary) => (m.date ? Date.parse(m.date) : 0);

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "date-asc":
          return dateKey(a) - dateKey(b);
        case "from-asc":
          return fromKey(a).localeCompare(fromKey(b), "it") || dateKey(b) - dateKey(a);
        case "from-desc":
          return fromKey(b).localeCompare(fromKey(a), "it") || dateKey(b) - dateKey(a);
        case "subject-asc":
          return subKey(a).localeCompare(subKey(b), "it") || dateKey(b) - dateKey(a);
        case "subject-desc":
          return subKey(b).localeCompare(subKey(a), "it") || dateKey(b) - dateKey(a);
        case "date-desc":
        default:
          return dateKey(b) - dateKey(a);
      }
    });
    return filtered;
  }, [messages, sortBy, dateFilter, dateFrom, dateTo]);

  const virtualizer = useVirtualizer({
    count: viewMessages.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 82,
    overscan: 14,
  });

  const accountLabel =
    selectedAccountId === "all"
      ? "All accounts"
      : accounts.find((a) => a.id === selectedAccountId)?.email || "Account";

  const safeHtml = useMemo(() => {
    if (!detail?.html) return null;
    return DOMPurify.sanitize(detail.html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    });
  }, [detail]);

  const threaded = useMemo(() => groupThreads(viewMessages), [viewMessages]);

  return (
    <>
      {(blockingLoad || busy) && (
        <div className={`mail-progress ${blockingLoad ? "blocking" : ""}`} aria-hidden>
          <div className="mail-progress-bar" />
        </div>
      )}
      {blockingLoad && (
        <div className="mail-block-overlay" aria-busy="true" aria-live="polite">
          <span>Caricamento casella…</span>
        </div>
      )}
      <div
        ref={workspaceRef}
        className={`workspace show-${mobile} ${railExpanded ? "rail-open" : "rail-closed"}`}
        style={
          {
            "--folder": `${folderWidth}px`,
            "--list": `${listWidth}px`,
          } as CSSProperties
        }
      >
        <aside className={`pane rail ${railExpanded ? "expanded" : "collapsed"}`}>
          <button
            className="rail-toggle"
            title={railExpanded ? "Comprimi account" : "Espandi account"}
            onClick={() => setRailExpanded((v) => !v)}
            aria-expanded={railExpanded}
          >
            <span className="rail-toggle-icon">{railExpanded ? "‹" : "›"}</span>
            {railExpanded && <span>Account</span>}
          </button>
          <button
            className={`rail-item ${selectedAccountId === "all" ? "active" : ""}`}
            title="Tutti gli account"
            onClick={() => selectAccount("all")}
          >
            <span className="rail-avatar all">∀</span>
            {railExpanded && (
              <span className="rail-meta">
                <strong>Tutti</strong>
                <em>{accounts.length} account</em>
              </span>
            )}
          </button>
          <div className="rail-scroll">
            {accounts.map((a) => (
              <button
                key={a.id}
                type="button"
                draggable
                className={`rail-item ${selectedAccountId === a.id ? "active" : ""} ${
                  dragOverId === a.id ? "drag-over" : ""
                } ${dragAccountId === a.id ? "dragging" : ""}`}
                title={`${a.email} · trascina per riordinare`}
                onDragStart={(e) => {
                  setDragAccountId(a.id);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", a.id);
                }}
                onDragEnd={() => {
                  setDragAccountId(null);
                  setDragOverId(null);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverId(a.id);
                }}
                onDragLeave={() => {
                  setDragOverId((cur) => (cur === a.id ? null : cur));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  void onAccountDrop(a.id);
                }}
                onClick={() => selectAccount(a.id)}
              >
                <span className="rail-avatar" style={{ background: a.color }}>
                  {(a.displayName || a.email).slice(0, 1).toUpperCase()}
                </span>
                {railExpanded && (
                  <span className="rail-meta">
                    <strong>{a.displayName || a.email.split("@")[0]}</strong>
                    <em>{a.email}</em>
                  </span>
                )}
              </button>
            ))}
          </div>
          <button className="rail-item rail-add" title="Aggiungi account" onClick={() => setWizard(true)}>
            <span className="rail-avatar add">+</span>
            {railExpanded && (
              <span className="rail-meta">
                <strong>Aggiungi</strong>
                <em>Nuovo account</em>
              </span>
            )}
          </button>
        </aside>

        <aside className="pane folders">
          <div className="pane-head folders-head">
            <div>
              <div className="eyebrow">Mailbox</div>
              <strong>{accountLabel}</strong>
            </div>
          </div>
          <button type="button" className="btn btn-primary btn-compose-wide" onClick={() => setComposer(true)}>
            Nuovo messaggio
          </button>
          <div className="folders-body">
            <div className="eyebrow" style={{ padding: "0.35rem 0.65rem" }}>
              Smart
            </div>
            {(
              [
                ["", "All in folder"],
                ["unread", "Unread"],
                ["starred", "Starred"],
                ["attachments", "Attachments"],
                ["today", "Today"],
              ] as Array<[SmartId, string]>
            ).map(([id, label]) => (
              <button
                key={id || "all"}
                className={`smart-btn ${smart === id ? "active" : ""}`}
                onClick={() => {
                  setSmart(id);
                  setMobile("list");
                }}
              >
                {label}
              </button>
            ))}
            <div className="eyebrow" style={{ padding: "0.75rem 0.75rem 0.35rem" }}>
              Cartelle
            </div>
            <div className="folder-list">
              {folders.map((f) => (
                <button
                  key={f.path}
                  className={`folder-btn ${folder === f.path && !smart ? "active" : ""}`}
                  onClick={() => {
                    setFolder(f.path);
                    setSmart("");
                    setSelected(null);
                    setDetail(null);
                    setMobile("list");
                  }}
                >
                  <span>{f.name}</span>
                  {typeof f.status?.unseen === "number" && f.status.unseen > 0 && (
                    <em className="badge">{f.status.unseen}</em>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div
            className="col-resizer"
            title="Trascina per ridimensionare"
            onMouseDown={(e) => startColResize("folder", e)}
          />
        </aside>

        <section className="pane list">
          <div className="pane-head list-toolbar">
            <input
              className="input"
              placeholder="Cerca mail"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void refreshMessages({ bustCache: true })}
            />
            <button
              className="btn btn-refresh"
              type="button"
              title="Ricarica messaggi"
              onClick={() => void refreshMessages({ bustCache: true })}
              disabled={busy || blockingLoad}
            >
              {busy ? "Carico…" : "Ricarica"}
            </button>
          </div>
          <div className="list-filters">
            <label className="filter-field">
              <span>Ordina</span>
              <select
                className="select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortId)}
              >
                <option value="date-desc">Data · più recenti</option>
                <option value="date-asc">Data · più vecchie</option>
                <option value="from-asc">Mittente A → Z</option>
                <option value="from-desc">Mittente Z → A</option>
                <option value="subject-asc">Oggetto A → Z</option>
                <option value="subject-desc">Oggetto Z → A</option>
              </select>
            </label>
            <label className="filter-field">
              <span>Periodo</span>
              <select
                className="select"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as DateFilterId)}
              >
                <option value="all">Tutte le date</option>
                <option value="today">Oggi</option>
                <option value="yesterday">Ieri</option>
                <option value="7d">Ultimi 7 giorni</option>
                <option value="30d">Ultimi 30 giorni</option>
                <option value="month">Questo mese</option>
                <option value="custom">Intervallo personalizzato</option>
              </select>
            </label>
            {dateFilter === "custom" && (
              <>
                <label className="filter-field">
                  <span>Dal</span>
                  <input className="input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </label>
                <label className="filter-field">
                  <span>Al</span>
                  <input className="input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </label>
              </>
            )}
            {(dateFilter !== "all" || sortBy !== "date-desc") && (
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => {
                  setSortBy("date-desc");
                  setDateFilter("all");
                  setDateFrom("");
                  setDateTo("");
                }}
              >
                Reset filtri
              </button>
            )}
          </div>
          {selectedIds.size > 0 && (
            <div className="pane-head actions">
              <button className="btn" onClick={() => void bulkAction("seen")}>
                Mark read
              </button>
              <button className="btn" onClick={() => void bulkAction("star")}>
                Star
              </button>
              <button className="btn btn-danger" onClick={() => void bulkAction("trash")}>
                Trash
              </button>
            </div>
          )}
          {error && <div className="error" style={{ padding: "0.6rem 1rem" }}>{error}</div>}
          <div style={{ padding: "0.4rem 1rem", color: "var(--muted)", fontSize: "0.78rem", display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
            <span>
              {viewMessages.length}
              {viewMessages.length !== total ? ` / ${total}` : ""} messaggi ·{" "}
              <strong style={{ color: "var(--unread)" }}>
                {viewMessages.filter((m) => !m.seen).length} non lette
              </strong>
              {" · "}
              {viewMessages.filter((m) => m.seen).length} lette · {folder}
              {smart ? ` · smart:${smart}` : ""}
              {dateFilter !== "all" ? ` · periodo:${dateFilter}` : ""}
            </span>
            {selectedAccountId !== "all" && viewMessages.some((m) => !m.seen) && (
              <button
                className="btn btn-sm"
                type="button"
                disabled={busy || blockingLoad}
                onClick={() => {
                  void (async () => {
                    if (
                      !window.confirm(
                        `Segnare come lette tutte le email non lette in «${folder}» su questo account? L’operazione scrive su IMAP.`,
                      )
                    ) {
                      return;
                    }
                    try {
                      setBusy(true);
                      const r = await api.markFolderSeen(selectedAccountId, folder);
                      toast.push({ message: `Segnate come lette: ${r.marked}` });
                      await refreshMessages({ bustCache: true });
                    } catch (e) {
                      toast.push({ message: e instanceof Error ? e.message : String(e) });
                    } finally {
                      setBusy(false);
                    }
                  })();
                }}
              >
                Segna cartella come letta
              </button>
            )}
          </div>
          <div className="msg-scroll" ref={listRef}>
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((row) => {
                const msg = viewMessages[row.index]!;
                const active = selected && keyOf(selected) === keyOf(msg);
                const checked = selectedIds.has(keyOf(msg));
                return (
                  <div
                    key={keyOf(msg)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${row.size}px`,
                      transform: `translateY(${row.start}px)`,
                      display: "grid",
                      gridTemplateColumns: "28px 1fr",
                      alignItems: "stretch",
                    }}
                  >
                    <label style={{ display: "grid", placeItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(keyOf(msg));
                            else next.delete(keyOf(msg));
                            return next;
                          });
                        }}
                      />
                    </label>
                    <button
                      className={`msg-row ${active ? "active" : ""} ${msg.seen ? "" : "unread"}`}
                      onClick={() => void openMessage(msg)}
                    >
                      <div className="msg-top">
                        <span className="msg-avatar" aria-hidden>
                          {(msg.from || msg.accountEmail || "?")
                            .replace(/^"|"$/g, "")
                            .trim()
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                        <div className="msg-from-wrap">
                          <strong className="msg-from">{msg.from || msg.accountEmail}</strong>
                          {selectedAccountId === "all" && (
                            <span
                              className="msg-account-chip"
                              style={{
                                borderColor:
                                  accounts.find((a) => a.id === msg.accountId)?.color || "var(--accent)",
                              }}
                            />
                          )}
                        </div>
                        <time>
                          {msg.date
                            ? new Date(msg.date).toLocaleString(undefined, {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </time>
                      </div>
                      <div className="sub">{msg.subject || "(senza oggetto)"}</div>
                      <div className="snip">{msg.snippet}</div>
                      {(msg.flagged || msg.hasAttachments) && (
                        <div className="msg-flags">
                          {msg.flagged && <span className="flag-star" title="Stellato">★</span>}
                          {msg.hasAttachments && <span className="flag-att" title="Allegati">‖</span>}
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
            {viewMessages.length === 0 && !busy && (
              <div className="empty">
                <h2>{accounts.length ? "Nessun messaggio" : "Collega un account"}</h2>
                <p>
                  {accounts.length
                    ? dateFilter !== "all" || search.trim()
                      ? "Prova a cambiare periodo, ordinamento o ricerca."
                      : "Prova un’altra cartella o togli i filtri smart."
                    : "Aggiungi Gmail, Microsoft o IMAP per iniziare."}
                </p>
                {!accounts.length && (
                  <button className="btn btn-primary" onClick={() => setWizard(true)}>
                    Add account
                  </button>
                )}
              </div>
            )}
          </div>
          <div
            className="col-resizer"
            title="Trascina per ridimensionare"
            onMouseDown={(e) => startColResize("list", e)}
          />
        </section>

        <section className="pane read">
          {!detail || !selected ? (
            <div className="empty read-empty">
              <h2>Seleziona un messaggio</h2>
              <p>j/k naviga · c compone · e archivia · # elimina · ! spam · r rispondi · Ctrl+K comandi</p>
            </div>
          ) : (
            <>
              <div className="read-head">
                <div className="read-toolbar">
                  <button className="btn btn-ghost read-back" onClick={() => setMobile("list")}>
                    ← Lista
                  </button>
                  <div className="read-actions">
                    <button className="action-btn primary" onClick={() => setComposer(true)} title="Rispondi (r)">
                      <span className="action-ico">↩</span>
                      <span>Rispondi</span>
                    </button>
                    <button className="action-btn" onClick={() => void toggleStar()} title="Preferito (s)">
                      <span className="action-ico">{selected.flagged ? "★" : "☆"}</span>
                      <span>{selected.flagged ? "Togli stella" : "Stella"}</span>
                    </button>
                    <button className="action-btn" onClick={() => void archiveSelected()} title="Archivia (e)">
                      <span className="action-ico">▤</span>
                      <span>Archivia</span>
                    </button>
                    <button className="action-btn" onClick={() => void toggleSeen()} title="Letto/non letto (u)">
                      <span className="action-ico">{selected.seen ? "◌" : "●"}</span>
                      <span>{selected.seen ? "Non letto" : "Letto"}</span>
                    </button>
                    <button className="action-btn warn" onClick={() => void spamSelected()} title="Spam (!)">
                      <span className="action-ico">⚠</span>
                      <span>Spam</span>
                    </button>
                    <button className="action-btn danger" onClick={() => void trashSelected()} title="Elimina (#)">
                      <span className="action-ico">⌫</span>
                      <span>Elimina</span>
                    </button>
                  </div>
                </div>
                <h1>{detail.subject || "(senza oggetto)"}</h1>
                <div className="read-identity">
                  <span
                    className="rail-avatar"
                    style={{
                      background: accounts.find((a) => a.id === selected.accountId)?.color || "var(--accent)",
                    }}
                  >
                    {(detail.from || "?").replace(/"/g, "").trim().slice(0, 1).toUpperCase()}
                  </span>
                  <div className="meta">
                    <div>
                      <span>Da</span> {detail.from}
                    </div>
                    <div>
                      <span>A</span> {detail.to}
                    </div>
                    <div>
                      <span>Data</span> {detail.date ? new Date(detail.date).toLocaleString() : "—"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="read-body">
                {safeHtml ? (
                  <iframe
                    className="read-frame"
                    title="message"
                    sandbox=""
                    referrerPolicy="no-referrer"
                    srcDoc={`<!doctype html><html><head><meta charset="utf-8"/><base target="_blank"/><style>body{font-family:Georgia,'Times New Roman',serif;color:#1c1812;line-height:1.55;margin:20px 22px;background:#fffdf8}img{max-width:100%;height:auto}a{color:#9a4f2d}pre,code{font-family:ui-monospace,monospace;font-size:0.92em}</style></head><body>${safeHtml}</body></html>`}
                  />
                ) : (
                  <pre className="read-plain">{detail.text || "(vuoto)"}</pre>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      <nav className="mobile-nav">
        <button className={mobile === "nav" ? "active" : ""} onClick={() => setMobile("nav")}>
          Accounts
        </button>
        <button className={mobile === "list" ? "active" : ""} onClick={() => setMobile("list")}>
          Inbox
        </button>
        <button className={mobile === "read" ? "active" : ""} onClick={() => setMobile("read")}>
          Read
        </button>
        <button onClick={() => setComposer(true)}>Compose</button>
        <button onClick={() => setWizard(true)}>Add</button>
      </nav>

      {composer && (
        <ComposerModal
          accounts={accounts}
          defaultAccountId={selectedAccountId !== "all" ? selectedAccountId : accounts[0]?.id}
          replyTo={detail}
          onClose={() => setComposer(false)}
          onSent={() => {
            setComposer(false);
            toast.push({ message: "Message sent" });
            void refreshMessages();
          }}
        />
      )}
      {wizard && (
        <AccountWizard
          onClose={() => setWizard(false)}
          onAdded={async () => {
            setWizard(false);
            await refreshAccounts();
            toast.push({ message: "Account connected" });
          }}
        />
      )}
    </>
  );
}

function groupThreads(messages: MailMessageSummary[]) {
  // Lightweight thread estimate by normalized subject
  const map = new Map<string, number>();
  for (const m of messages) {
    const key = (m.subject || "")
      .replace(/^(re|fw|fwd):\s*/gi, "")
      .trim()
      .toLowerCase();
    map.set(key, (map.get(key) || 0) + 1);
  }
  return [...map.entries()];
}
